import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Layout, Modal, ImageWithFallback } from '../../../shared/components'
import { useCssHandles, CssHandles } from '../../../shared/hooks/useCssHandles'
import { useOrders, useOrder } from '../../../hooks/useOrders'
import { orderService, Order, OrderStatus, PaymentMethod, PaymentStatus } from '../../../services'
import { getPaymentDetailsByOrderId } from '../../payment/services/paymentService'
import { PixPayment, BoletoPayment } from '../../payment/components'
import { useAuth } from '../../auth/contexts/AuthContext'
import { buildImageUrl } from '../../../config'
import { useToast } from '../../../contexts/ToastContext'
import { colors, spacing } from '../../../styles/design-system'

type OrderStatusFilter = 'all' | OrderStatus

const isOrderStatus = (value: string): value is OrderStatus =>
  Object.values(OrderStatus).some((status) => status === value)

const normalizeOrderStatus = (status: Order['status'] | string): OrderStatus => {
  if (!status) return OrderStatus.PENDING
  const normalized = (typeof status === 'string' ? status.toLowerCase() : status) as string
  return isOrderStatus(normalized) ? (normalized as unknown as OrderStatus) : OrderStatus.PENDING
}

const CSS_HANDLES = [
  'ordersPage',
  'ordersHeader',
  'ordersTitle',
  'ordersSubtitle',
  'ordersFilters',
  'ordersFilterButton',
  'ordersList',
  'ordersCard',
  'ordersCardHeader',
  'ordersCardBody',
  'ordersCardFooter',
  'ordersAmount',
  'ordersStatusBadge',
  'ordersEmpty',
  'ordersError',
  'ordersSkeleton',
  'ordersPagination',
  'ordersPaginationButton',
  'ordersModal',
  'ordersModalSection',
  'ordersTimeline',
  'ordersItem',
  'ordersSummaryRow',
] as const

const STATUS_OPTIONS: Array<{ value: OrderStatusFilter; label: string }> = [
  { value: 'all', label: 'Todos' },
  { value: OrderStatus.PENDING, label: 'Pendentes' },
  { value: OrderStatus.PROCESSING, label: 'Processando' },
  { value: OrderStatus.SHIPPED, label: 'Enviados' },
  { value: OrderStatus.DELIVERED, label: 'Entregues' },
  { value: OrderStatus.CANCELLED, label: 'Cancelados' },
]

const STATUS_LABELS: Record<OrderStatus, string> = {
  [OrderStatus.PENDING]: 'Pendente',
  [OrderStatus.PROCESSING]: 'Processando',
  [OrderStatus.SHIPPED]: 'Enviado',
  [OrderStatus.DELIVERED]: 'Entregue',
  [OrderStatus.CANCELLED]: 'Cancelado',
  [OrderStatus.RETURNED]: 'Devolvido',
  [OrderStatus.CONFIRMED]: 'Confirmado',
  [OrderStatus.READY_FOR_PICKUP]: 'Disponível para retirada'
}

const STATUS_COLORS: Record<OrderStatus, { text: string; background: string }> = {
  [OrderStatus.PENDING]: { text: colors.secondary.darkYellow, background: colors.secondary.lightYellow },
  [OrderStatus.PROCESSING]: { text: colors.primary.blue, background: colors.secondary.lightBlue },
  [OrderStatus.SHIPPED]: { text: colors.status.info, background: '#E0F7FA' },
  [OrderStatus.DELIVERED]: { text: colors.status.success, background: '#E9F7EF' },
  [OrderStatus.CANCELLED]: { text: colors.status.error, background: '#FDECEA' },
  [OrderStatus.RETURNED]: { text: colors.status.warning, background: '#FFF3CD' },
  [OrderStatus.CONFIRMED]: { text: colors.status.success, background: '#E9F7EF' },
  [OrderStatus.READY_FOR_PICKUP]: { text: colors.primary.blue, background: colors.secondary.lightBlue }
}

const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  [PaymentMethod.PIX]: 'PIX',
  [PaymentMethod.CREDIT_CARD]: 'Cartão de Crédito',
  [PaymentMethod.DEBIT_CARD]: 'Cartão de Débito',
  [PaymentMethod.BOLETO]: 'Boleto Bancário',
}

const normalizePaymentMethod = (method?: string) => {
  if (!method) return null
  return method.trim().toLowerCase().replace(/[\s-]+/g, '_')
}

const formatFlagFallback = (value: string) =>
  value
    .split(/[_\s-]+/g)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
    .join(' ')

const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  [PaymentStatus.PENDING]: 'Pagamento pendente',
  [PaymentStatus.PROCESSING]: 'Processando pagamento',
  [PaymentStatus.AUTHORIZED]: 'Pagamento autorizado',
  [PaymentStatus.PAID]: 'Pagamento concluído',
  [PaymentStatus.COMPLETED]: 'Pagamento concluído',
  [PaymentStatus.REFUNDED]: 'Pagamento reembolsado',
  [PaymentStatus.FAILED]: 'Pagamento não concluído',
  [PaymentStatus.CANCELLED]: 'Pagamento cancelado',
  [PaymentStatus.CANCELED]: 'Pagamento cancelado',
  [PaymentStatus.CHARGEBACK]: 'Chargeback recebido',
}

const normalizePaymentStatus = (status?: string) => {
  if (!status) return null
  return status.trim().toLowerCase().replace(/[\s-]+/g, '_')
}

const PAGE_SIZE = 6

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)

const formatCurrencyOrPlaceholder = (value?: number | null, placeholder = '—') =>
  typeof value === 'number' && Number.isFinite(value) ? formatCurrency(value) : placeholder

const formatDate = (value?: string | null) => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('pt-BR')
}

const getPaymentMethodLabel = (method?: string) => {
  if (!method) return 'Método não informado'
  const normalized = normalizePaymentMethod(method)
  if (normalized && PAYMENT_METHOD_LABELS[normalized as PaymentMethod]) {
    return PAYMENT_METHOD_LABELS[normalized as PaymentMethod]
  }
  return formatFlagFallback(method)
}

const getPaymentStatusLabel = (status?: string) => {
  if (!status) return 'Status não informado'
  const normalized = normalizePaymentStatus(status)
  if (normalized && PAYMENT_STATUS_LABELS[normalized as PaymentStatus]) {
    return PAYMENT_STATUS_LABELS[normalized as PaymentStatus]
  }
  return formatFlagFallback(status)
}

const getEstimatedDeliveryText = (order: Order) => {
  const isPickup = order.shippingMethod === 'pickup'
  const status = normalizeOrderStatus(order.status)
  
  if (status === 'delivered') {
    return isPickup ? 'Pedido retirado' : 'Pedido entregue'
  }
  
  if (!order.estimatedDelivery) {
    return isPickup 
      ? 'Previsão de retirada não disponível' 
      : 'Previsão de entrega não disponível'
  }
  
  return `${isPickup ? 'Previsão de retirada' : 'Previsão de entrega'}: ${formatDate(order.estimatedDelivery)}`
}

type OrderLineItem = Order['items'][number]

type OrderItemProduct = {
  name?: string
  image?: string
  images?: string[] | Array<{ imageUrl: string }>
  brand?: string
  capacity?: string
  [key: string]: unknown
}

const getOrderItemProduct = (item: OrderLineItem): OrderItemProduct | null => {
  if (item && typeof item.product === 'object' && item.product !== null) {
    return item.product as OrderItemProduct
  }
  return null
}

const getNonEmptyString = (value: unknown) =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null

const getFiniteNumber = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? value : null)

const getOrderItemName = (item: OrderLineItem) =>
  getNonEmptyString(item.productName) ?? getNonEmptyString(getOrderItemProduct(item)?.name) ?? 'Produto'

const getOrderItemImage = (item: OrderLineItem) => {
  const directImage = getNonEmptyString(item.productImage) ?? getNonEmptyString(getOrderItemProduct(item)?.image)
  
  if (directImage) {
    return buildImageUrl(directImage)
  }

  const product = getOrderItemProduct(item)
  if (product?.images && Array.isArray(product.images) && product.images.length > 0) {
    const firstImage = product.images[0]
    if (typeof firstImage === 'string') {
      return buildImageUrl(firstImage)
    }
    if (typeof firstImage === 'object' && firstImage !== null && 'imageUrl' in firstImage) {
      return buildImageUrl((firstImage as { imageUrl: string }).imageUrl)
    }
  }

  return undefined
}

const getOrderItemBrand = (item: OrderLineItem) =>
  getNonEmptyString(item.brand) ?? getNonEmptyString(getOrderItemProduct(item)?.brand)

const getOrderItemCapacity = (item: OrderLineItem) => {
  const capacityStr = getNonEmptyString(item.capacity) ?? getNonEmptyString(getOrderItemProduct(item)?.capacity)
  if (!capacityStr) return null
  // Verificar se a capacidade é "0", "0Ah" ou similar
  const capacityNum = parseFloat(capacityStr.replace(/[^0-9.]/g, ''))
  if (isNaN(capacityNum) || capacityNum <= 0) return null
  return capacityStr
}

const getOrderItemUnitPrice = (item: OrderLineItem) => {
  const unitPrice = getFiniteNumber(item.unitPrice)
  if (unitPrice !== null) return unitPrice

  const legacyPrice = getFiniteNumber(item.price)
  if (legacyPrice !== null) return legacyPrice

  const totalPrice = getFiniteNumber(item.totalPrice)
  const quantity = getFiniteNumber(item.quantity) ?? 0
  if (totalPrice !== null && quantity > 0) {
    const unit = totalPrice / quantity
    return Number.isFinite(unit) ? unit : null
  }

  return null
}

const getOrderItemTotalPrice = (item: OrderLineItem) => {
  const totalPrice = getFiniteNumber(item.totalPrice)
  if (totalPrice !== null) return totalPrice

  const quantity = getFiniteNumber(item.quantity) ?? 0
  const unitPrice = getOrderItemUnitPrice(item)
  if (unitPrice !== null && quantity > 0) {
    return unitPrice * quantity
  }

  return null
}

const Orders: React.FC = () => {
  const handles = useCssHandles(CSS_HANDLES)
  const [statusFilter, setStatusFilter] = useState<OrderStatusFilter>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const [paymentViewOrder, setPaymentViewOrder] = useState<Order | null>(null)
  const { user } = useAuth()
  const { showSuccess, showError, showInfo } = useToast()

  const params = useMemo(
    () => ({
      page: currentPage,
      limit: PAGE_SIZE,
      status: statusFilter === 'all' ? undefined : statusFilter,
    }),
    [currentPage, statusFilter],
  )

  const { orders, loading, error, pagination, reload } = useOrders(params)
  const {
    order: detailedOrder,
    loading: detailLoading,
    error: detailError,
  } = useOrder(selectedOrderId ?? undefined)

  const activeOrder =
    detailedOrder ?? orders.find((order) => order.id === selectedOrderId) ?? null
  const totalPages = Math.max(pagination.totalPages || 1, 1)

  const handleStatusChange = (value: OrderStatusFilter) => {
    setStatusFilter(value)
    setCurrentPage(1)
  }

  const handlePaginate = (page: number) => {
    setCurrentPage(page)
  }

  const handleOpenOrder = (orderId: string) => {
    setSelectedOrderId(orderId)
  }

  const handleCloseModal = () => {
    setSelectedOrderId(null)
  }

  const handleOpenPaymentView = (order: Order) => {
    setPaymentViewOrder(order)
  }

  const handleClosePaymentView = () => {
    setPaymentViewOrder(null)
  }

  const handleCancelOrder = useCallback(
    async (orderId: string) => {
      try {
        setIsCancelling(true)
        await orderService.cancel(orderId)
        showSuccess('Pedido cancelado', 'Seu pedido foi cancelado com sucesso.')
        handleCloseModal()
        reload()
      } catch (err) {
        console.error('Erro ao cancelar pedido', err)
        const message =
          err instanceof Error
            ? err.message
            : 'Não foi possível cancelar o pedido. Tente novamente mais tarde.'
        showError('Erro ao cancelar pedido', message)
      } finally {
        setIsCancelling(false)
      }
    },
    [reload, showError, showSuccess],
  )

  const handleReorder = useCallback(
    (order: Order) => {
      const orderLabel = order.orderNumber || order.id
      showInfo(
        'Solicitação enviada',
        `Vamos analisar o pedido ${orderLabel} e preparar o reagendamento para você.`,
      )
    },
    [showInfo],
  )

  const renderSkeletons = () =>
    Array.from({ length: 3 }).map((_, index) => (
      <div
        key={`order-skeleton-${index}`}
        className={`${handles.ordersSkeleton} animate-pulse rounded-2xl border border-gray-100 bg-white p-6 shadow-sm`}
      >
        <div className="flex flex-col gap-4">
          <div className="flex justify-between">
            <div className="h-4 w-32 rounded bg-gray-200" />
            <div className="h-4 w-24 rounded bg-gray-200" />
          </div>
          <div className="h-3 w-24 rounded bg-gray-200" />
          <div className="grid gap-3 md:grid-cols-3">
            {Array.from({ length: 3 }).map((__, itemIndex) => (
              <div key={`order-skeleton-item-${itemIndex}`} className="flex items-center gap-3">
                <div className="h-14 w-14 rounded-lg bg-gray-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-32 rounded bg-gray-200" />
                  <div className="h-3 w-24 rounded bg-gray-200" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    ))

  return (
    <Layout>
      <div
        className={`${handles.ordersPage} bg-gray-50`}
        style={{ minHeight: '100vh', padding: `${spacing['2xl']} ${spacing.lg}` }}
      >
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-10">
          <header className={`${handles.ordersHeader} flex flex-col gap-2`}>
            <h1
              className={`${handles.ordersTitle} text-3xl font-bold`}
              style={{ color: colors.neutral.gray900 }}
            >
              Meus Pedidos
            </h1>
            <p
              className={`${handles.ordersSubtitle} text-base`}
              style={{ color: colors.neutral.gray600 }}
            >
              Visualize seus pedidos, acompanhe o status e gerencie ações importantes.
            </p>
          </header>

          <nav className={`${handles.ordersFilters} flex flex-wrap gap-2`}>
            {STATUS_OPTIONS.map((option) => {
              const isActive = statusFilter === option.value
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => handleStatusChange(option.value)}
                  className={`${handles.ordersFilterButton} flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors`}
                  style={{
                    borderColor: isActive ? colors.primary.blue : colors.neutral.gray200,
                    backgroundColor: isActive ? colors.primary.blue : colors.neutral.white,
                    color: isActive ? colors.neutral.white : colors.neutral.gray700,
                  }}
                >
                  {option.label}
                </button>
              )
            })}
          </nav>

          <section className={`${handles.ordersList} flex flex-col gap-6`}>
            {loading && renderSkeletons()}

            {!loading && error && (
              <div
                className={`${handles.ordersError} rounded-2xl border border-red-100 bg-red-50 p-10 text-center shadow-sm`}
              >
                <h2 className="text-lg font-semibold text-red-700">
                  Não foi possível carregar seus pedidos
                </h2>
                <p className="mt-2 text-sm text-red-600">
                  {error || 'Ocorreu um problema momentâneo. Tente novamente.'}
                </p>
                <button
                  type="button"
                  onClick={reload}
                  className="mt-6 inline-flex items-center rounded-full px-5 py-2 text-sm font-semibold transition-colors"
                  style={{
                    backgroundColor: colors.primary.blue,
                    color: colors.neutral.white,
                  }}
                >
                  Tentar novamente
                </button>
              </div>
            )}

            {!loading && !error && orders.length === 0 && (
              <div
                className={`${handles.ordersEmpty} rounded-2xl border border-dashed border-gray-200 bg-white p-10 text-center shadow-sm`}
              >
                <h2 className="text-lg font-semibold" style={{ color: colors.neutral.gray800 }}>
                  Nenhum pedido encontrado
                </h2>
                <p className="mt-2 text-sm" style={{ color: colors.neutral.gray600 }}>
                  Assim que você realizar compras, elas aparecerão aqui para acompanhamento.
                </p>
              </div>
            )}

            {!loading &&
              !error &&
              orders.map((order) => {
                const normalizedStatus = normalizeOrderStatus(order.status)
                return (
                  <article
                    key={order.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpenOrder(order.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      handleOpenOrder(order.id)
                    }
                  }}
                  className={`${handles.ordersCard} group rounded-2xl border border-gray-100 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2`}
                  style={{ outlineColor: colors.primary.blue }}
                >
                  <div className="flex flex-col gap-6 p-6">
                    <header className={`${handles.ordersCardHeader} flex flex-wrap items-start gap-6`}>
                      <div className="flex flex-col gap-2">
                        <div className="flex flex-wrap items-center gap-3">
                          <span
                            className="text-sm font-semibold uppercase tracking-wide"
                            style={{ color: colors.neutral.gray500 }}
                          >
                            Pedido {order.orderNumber || order.id}
                          </span>
                          <StatusBadge status={normalizedStatus} className={handles.ordersStatusBadge} />
                        </div>
                        <span className="text-sm" style={{ color: colors.neutral.gray600 }}>
                          Realizado em {formatDate(order.date ?? order.createdAt)}
                        </span>
                      </div>

                      <div className="ml-auto flex flex-col items-end gap-1">
                        <strong
                          className={`${handles.ordersAmount} text-xl font-semibold`}
                          style={{ color: colors.primary.blue }}
                        >
                          {formatCurrency(order.total)}
                        </strong>
                        <span className="text-xs" style={{ color: colors.neutral.gray500 }}>
                          {getPaymentMethodLabel(order.paymentMethod)}
                        </span>
                      </div>
                    </header>

                    <div className={`${handles.ordersCardBody} flex flex-col gap-4`}>
                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {order.items.slice(0, 3).map((item) => (
                          <OrderItemPreview key={`${order.id}-${item.id}`} item={item} />
                        ))}
                      </div>

                      {order.items.length > 3 && (
                        <span className="text-sm font-medium" style={{ color: colors.primary.blue }}>
                          +{order.items.length - 3}{' '}
                          {order.items.length - 3 === 1 ? 'item adicional' : 'itens adicionais'}
                        </span>
                      )}
                    </div>

                    <footer
                      className={`${handles.ordersCardFooter} flex flex-wrap items-center justify-between gap-4 border-t border-gray-100 pt-4`}
                    >
                      <div className="flex flex-col gap-1">
                        {order.trackingCode && (
                          <span className="text-sm font-medium" style={{ color: colors.primary.blue }}>
                            Código de rastreamento: {order.trackingCode}
                          </span>
                        )}
                        <span className="text-xs" style={{ color: colors.neutral.gray600 }}>
                          {getEstimatedDeliveryText(order)}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation()
                          handleOpenOrder(order.id)
                        }}
                        className="inline-flex items-center rounded-full px-5 py-2 text-sm font-semibold transition-colors"
                        style={{
                          backgroundColor: colors.primary.blue,
                          color: colors.neutral.white,
                        }}
                      >
                        Ver detalhes
                      </button>
                    </footer>
                  </div>
                  </article>
                )
              })}
          </section>

          {!loading && !error && pagination.totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onChange={handlePaginate}
              handles={handles}
            />
          )}
        </div>
      </div>

      <OrderDetailsModal
        order={activeOrder}
        isOpen={Boolean(selectedOrderId)}
        isLoading={detailLoading}
        error={detailError}
        onClose={handleCloseModal}
        onCancel={handleCancelOrder}
        onReorder={handleReorder}
        onOpenPaymentView={handleOpenPaymentView}
        isCancelling={isCancelling}
        handles={handles}
      />

      <PaymentViewModal
        order={paymentViewOrder}
        isOpen={Boolean(paymentViewOrder)}
        onClose={handleClosePaymentView}
        user={user}
      />
    </Layout>
  )
}

type StatusBadgeProps = {
  status: OrderStatus | string
  className?: string
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, className }) => {
  const normalizedStatus = normalizeOrderStatus(status)
  const palette = STATUS_COLORS[normalizedStatus]
  const label = STATUS_LABELS[normalizedStatus]
  const badgeStyle: React.CSSProperties = {
    backgroundColor: palette.background,
    color: palette.text,
    width: 'fit-content',
    minWidth: 'fit-content',
  }
  const badgeClassName = [
    'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold tracking-wide',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <span className={badgeClassName} style={badgeStyle}>
      {label}
    </span>
  )
}

type OrderItemPreviewProps = {
  item: Order['items'][number]
}

const OrderItemPreview: React.FC<OrderItemPreviewProps> = ({ item }) => {
  const itemName = getOrderItemName(item)
  const imageSource = getOrderItemImage(item)
  const unitPrice = getOrderItemUnitPrice(item)
  const quantity = getFiniteNumber(item.quantity) ?? item.quantity ?? 0
  const unitPriceLabel = formatCurrencyOrPlaceholder(unitPrice)

  return (
    <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3">
      <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg bg-gray-200">
        <ImageWithFallback
          src={imageSource ?? ''}
          alt={itemName}
          className="h-full w-full object-cover"
          fallbackWrapperClassName="flex h-full w-full items-center justify-center bg-white text-xl"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="truncate text-sm font-medium" style={{ color: colors.neutral.gray800 }}>
          {itemName}
        </span>
        <span className="text-xs" style={{ color: colors.neutral.gray500 }}>
          {quantity} × {unitPriceLabel}
        </span>
      </div>
    </div>
  )
}

type PaginationProps = {
  currentPage: number
  totalPages: number
  onChange: (page: number) => void
  handles: CssHandles
}

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, onChange, handles }) => {
  const getButtonStyle = (active: boolean) => ({
    borderColor: active ? colors.primary.blue : colors.neutral.gray200,
    backgroundColor: active ? colors.primary.blue : colors.neutral.white,
    color: active ? colors.neutral.white : colors.neutral.gray700,
  })

  return (
    <nav
      className={`${handles.ordersPagination} flex items-center justify-center gap-2`}
      aria-label="Paginação de pedidos"
    >
      <button
        type="button"
        onClick={() => onChange(Math.max(currentPage - 1, 1))}
        disabled={currentPage === 1}
        className={`${handles.ordersPaginationButton} inline-flex items-center rounded-full border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60`}
        style={getButtonStyle(false)}
      >
        Anterior
      </button>
      {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
        <button
          key={page}
          type="button"
          onClick={() => onChange(page)}
          className={`${handles.ordersPaginationButton} inline-flex items-center rounded-full border px-4 py-2 text-sm font-medium transition-colors`}
          style={getButtonStyle(page === currentPage)}
        >
          {page}
        </button>
      ))}
      <button
        type="button"
        onClick={() => onChange(Math.min(currentPage + 1, totalPages))}
        disabled={currentPage === totalPages}
        className={`${handles.ordersPaginationButton} inline-flex items-center rounded-full border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60`}
        style={getButtonStyle(false)}
      >
        Próximo
      </button>
    </nav>
  )
}

type OrderDetailsModalProps = {
  order: Order | null
  isOpen: boolean
  isLoading: boolean
  error: string | null
  onClose: () => void
  onCancel: (orderId: string) => void | Promise<void>
  onReorder: (order: Order) => void
  onOpenPaymentView: (order: Order) => void
  isCancelling: boolean
  handles: CssHandles
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  order,
  isOpen,
  isLoading,
  error,
  onClose,
  onCancel,
  onReorder,
  onOpenPaymentView,
  isCancelling,
  handles,
}) => {
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false)
  const orderStatus = order ? normalizeOrderStatus(order.status) : null
  const isPickup = order?.shippingMethod === 'pickup'
  
  const handleOpenPaymentView = (order: Order) => {
    onOpenPaymentView(order)
  }

  const timelineSteps = useMemo(() => {
    const steps = [
      { key: OrderStatus.PENDING, label: 'Pedido realizado' },
      { key: OrderStatus.PROCESSING, label: 'Processando pagamento' },
      { key: OrderStatus.CONFIRMED, label: 'Pedido confirmado' },
    ]

    if (isPickup) {
      steps.push({ key: OrderStatus.READY_FOR_PICKUP, label: 'Disponível para retirada' })
      steps.push({ key: OrderStatus.DELIVERED, label: 'Pedido retirado' })
    } else {
      steps.push({ key: OrderStatus.SHIPPED, label: 'Pedido enviado' })
      steps.push({ key: OrderStatus.DELIVERED, label: 'Pedido entregue' })
    }

    return steps
  }, [isPickup])

  const canReorder = orderStatus === OrderStatus.DELIVERED
  const canCancel = orderStatus ? [OrderStatus.PENDING, OrderStatus.PROCESSING].includes(orderStatus) : false
  const timelineIndex = orderStatus ? timelineSteps.findIndex((item) => item.key === orderStatus) : -1

  const handleOpenConfirmCancel = () => {
    if (!isCancelling) {
      setConfirmCancelOpen(true)
    }
  }

  const handleCloseConfirmCancel = () => {
    if (!isCancelling) {
      setConfirmCancelOpen(false)
    }
  }

  const handleConfirmCancel = () => {
    if (!order || isCancelling) return
    const result = onCancel(order.id)
    if (result && typeof (result as Promise<unknown>).finally === 'function') {
      (result as Promise<unknown>).finally(() => setConfirmCancelOpen(false))
    } else {
      setConfirmCancelOpen(false)
    }
  }

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={order ? `Pedido ${order.orderNumber || order.id}` : 'Detalhes do Pedido'}
      size="xl"
      bodyStyle={{ backgroundColor: colors.neutral.white }}
      contentClassName={handles.ordersModal}
      footer={
        order && (
          <div className="flex flex-wrap items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full px-4 py-2 text-sm font-semibold transition-colors"
              style={{
                backgroundColor: colors.neutral.gray200,
                color: colors.neutral.gray700,
              }}
            >
              Fechar
            </button>

            {canReorder && (
              <button
                type="button"
                onClick={() => onReorder(order)}
                className="rounded-full px-4 py-2 text-sm font-semibold transition-colors"
                style={{
                  backgroundColor: colors.status.success,
                  color: colors.neutral.white,
                }}
              >
                Reordenar pedido
              </button>
            )}

            {canCancel && (
              <button
                type="button"
                onClick={handleOpenConfirmCancel}
                disabled={isCancelling}
                className="rounded-full px-4 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-70"
                style={{
                  backgroundColor: colors.status.error,
                  color: colors.neutral.white,
                }}
              >
                {isCancelling ? 'Cancelando...' : 'Cancelar pedido'}
              </button>
            )}

            {order &&
              (order.paymentStatus?.toLowerCase() === PaymentStatus.PENDING || order.paymentStatus?.toLowerCase() === PaymentStatus.FAILED || order.paymentStatus?.toLowerCase() === 'failed' || order.paymentStatus?.toLowerCase() === 'overdue') &&
              (order.paymentMethod?.toLowerCase() === PaymentMethod.PIX || order.paymentMethod?.toLowerCase() === PaymentMethod.BOLETO || order.paymentMethod?.toLowerCase() === 'pix' || order.paymentMethod?.toLowerCase() === 'boleto') && (
                <button
                  type="button"
                  onClick={() => handleOpenPaymentView(order)}
                  className="rounded-full px-4 py-2 text-sm font-semibold transition-colors"
                  style={{
                    backgroundColor: colors.primary.blue,
                    color: colors.neutral.white,
                  }}
                >
                  {order.paymentMethod?.toLowerCase() === PaymentMethod.PIX || order.paymentMethod?.toLowerCase() === 'pix' ? 'Visualizar QR Code PIX' : 'Visualizar Boleto'}
                </button>
              )}
          </div>
        )
      }
    >
    {isLoading && (
      <div className="space-y-6">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={`modal-skeleton-${index}`} className="animate-pulse rounded-2xl bg-gray-50 p-6">
            <div className="h-4 w-40 rounded bg-gray-200" />
            <div className="mt-3 h-3 w-64 rounded bg-gray-200" />
          </div>
        ))}
      </div>
    )}

    {!isLoading && error && (
      <div className="rounded-2xl border border-red-100 bg-red-50 p-6 text-center">
        <h3 className="text-lg font-semibold text-red-700">Não foi possível carregar o pedido</h3>
        <p className="mt-2 text-sm text-red-600">
          {error || 'Tente novamente em alguns instantes.'}
        </p>
      </div>
    )}

    {!isLoading && order && (
      <div className="flex flex-col gap-8">
        <section
          className={`${handles.ordersModalSection} grid gap-6 rounded-2xl border border-gray-100 bg-gray-50 p-6 md:grid-cols-3`}
        >
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Status do pedido
            </span>
            <StatusBadge status={orderStatus ?? 'pending'} className={handles.ordersStatusBadge} />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Data do pedido
            </span>
            <span className="text-sm font-medium" style={{ color: colors.neutral.gray800 }}>
              {formatDate(order.date ?? order.createdAt)}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Valor total
            </span>
            <span className="text-lg font-semibold" style={{ color: colors.primary.blue }}>
              {formatCurrency(order.total)}
            </span>
          </div>
        </section>

        <section className={`${handles.ordersTimeline} space-y-4`}>
          <h3 className="text-lg font-semibold" style={{ color: colors.neutral.gray900 }}>
            Linha do tempo
          </h3>
          <div className="flex flex-wrap gap-4 rounded-2xl border border-gray-100 bg-gray-50 p-6">
            {timelineSteps.map((step, index) => {
              const isCompleted = timelineIndex >= 0 && index <= timelineIndex
              return (
                <div key={step.key} className="flex items-center gap-3">
                  <span
                    className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold"
                    style={{
                      backgroundColor: isCompleted ? colors.primary.blue : colors.neutral.gray200,
                      color: isCompleted ? colors.neutral.white : colors.neutral.gray600,
                    }}
                  >
                    {isCompleted ? '✓' : index + 1}
                  </span>
                  <span className="text-sm" style={{ color: colors.neutral.gray700 }}>
                    {step.label}
                  </span>
                  {index < timelineSteps.length - 1 && (
                    <span className="h-px w-10 bg-gray-200" aria-hidden="true" />
                  )}
                </div>
              )
            })}
          </div>
        </section>

        <section className={`${handles.ordersModalSection} space-y-4`}>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold" style={{ color: colors.neutral.gray900 }}>
              Itens do pedido
            </h3>
            <span className="text-sm" style={{ color: colors.neutral.gray600 }}>
              {order.items.length} {order.items.length === 1 ? 'item' : 'itens'}
            </span>
          </div>

            <div className="flex flex-col gap-4">
              {order.items.map((item) => {
                const itemName = getOrderItemName(item)
                const imageSource = getOrderItemImage(item)
                const brand = getOrderItemBrand(item)
                const capacity = getOrderItemCapacity(item)
                const quantity = getFiniteNumber(item.quantity) ?? item.quantity ?? 0
                const unitPrice = getOrderItemUnitPrice(item)
                const totalPrice = getOrderItemTotalPrice(item)
                const unitPriceLabel = formatCurrencyOrPlaceholder(unitPrice)
                const totalPriceLabel = formatCurrencyOrPlaceholder(totalPrice)

                return (
                  <div
                    key={`${order.id}-${item.id}`}
                    className={`${handles.ordersItem} flex flex-col gap-4 rounded-2xl border border-gray-100 bg-white p-4 md:flex-row md:items-center`}
                  >
                    <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                      <ImageWithFallback
                        src={imageSource ?? ''}
                        alt={itemName}
                        className="h-full w-full object-cover"
                        fallbackWrapperClassName="flex h-full w-full items-center justify-center bg-white text-xl"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="truncate text-sm font-semibold" style={{ color: colors.neutral.gray900 }}>
                        {itemName}
                      </h4>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        {brand && (
                          <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                            {brand}
                          </span>
                        )}
                        {capacity && (
                          <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                            {capacity}
                          </span>
                        )}
                      </div>
                      <div className="mt-2 flex flex-col gap-1 text-xs" style={{ color: colors.neutral.gray500 }}>
                        <span>Quantidade: {quantity}</span>
                        <span>Valor unitário: {unitPriceLabel}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        Total
                      </span>
                      <p className="text-sm font-semibold" style={{ color: colors.primary.blue }}>
                        {totalPriceLabel}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
        </section>

        <section className={`${handles.ordersModalSection} grid gap-6 ${isPickup ? 'md:grid-cols-1' : 'md:grid-cols-2'}`}>
          {!isPickup && (
            <div className="flex flex-col gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-6">
              <h3 className="text-lg font-semibold" style={{ color: colors.neutral.gray900 }}>
                Endereço de entrega
              </h3>
              {order.shippingAddress ? (
                <address className="not-italic text-sm leading-relaxed" style={{ color: colors.neutral.gray700 }}>
                  {order.shippingAddress.name}
                  <br />
                  {order.shippingAddress.street}, {order.shippingAddress.number}
                  {order.shippingAddress.complement ? `, ${order.shippingAddress.complement}` : ''}
                  <br />
                  {order.shippingAddress.neighborhood} - {order.shippingAddress.city}/
                  {order.shippingAddress.state}
                  <br />
                  CEP: {order.shippingAddress.zipCode}
                </address>
              ) : (
                <p className="text-sm" style={{ color: colors.neutral.gray500 }}>
                  Endereço de entrega não disponível.
                </p>
              )}
            </div>
          )}

          <div className="flex flex-col gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-6">
            <h3 className="text-lg font-semibold" style={{ color: colors.neutral.gray900 }}>
              Pagamento & {isPickup ? 'retirada' : 'entrega'}
            </h3>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Método de pagamento
              </span>
              <span className="text-sm font-medium" style={{ color: colors.neutral.gray700 }}>
                {getPaymentMethodLabel(order.paymentMethod)}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Status do pagamento
              </span>
              <span className="text-sm font-medium" style={{ color: colors.neutral.gray700 }}>
                {getPaymentStatusLabel(order.paymentStatus)}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {isPickup ? 'Retirada prevista' : 'Entrega estimada'}
              </span>
              <span className="text-sm font-medium" style={{ color: colors.neutral.gray700 }}>
                {getEstimatedDeliveryText(order)}
              </span>
            </div>
          </div>
        </section>

        <section className={`${handles.ordersModalSection} rounded-2xl border border-gray-100 bg-gray-50 p-6`}>
          <h3 className="text-lg font-semibold" style={{ color: colors.neutral.gray900 }}>
            Resumo financeiro
          </h3>
          <div className="mt-4 flex flex-col gap-3">
            <div className={`${handles.ordersSummaryRow} flex justify-between text-sm`}>
              <span style={{ color: colors.neutral.gray600 }}>Subtotal</span>
              <span style={{ color: colors.neutral.gray700 }}>{formatCurrency(order.subtotal)}</span>
            </div>
            <div className={`${handles.ordersSummaryRow} flex justify-between text-sm`}>
              <span style={{ color: colors.neutral.gray600 }}>Frete</span>
              <span
                style={{
                  color: order.shipping === 0 ? colors.status.success : colors.neutral.gray700,
                }}
              >
                {order.shipping === 0 ? 'Grátis' : formatCurrency(order.shipping)}
              </span>
            </div>
            <div className={`${handles.ordersSummaryRow} flex justify-between text-sm`}>
              <span style={{ color: colors.neutral.gray600 }}>Desconto</span>
              <span style={{ color: colors.status.success }}>
                {order.discount > 0 ? `-${formatCurrency(order.discount)}` : formatCurrency(0)}
              </span>
            </div>
            <div className="h-px w-full bg-gray-200" aria-hidden="true" />
            <div className="flex justify-between text-base font-semibold" style={{ color: colors.primary.blue }}>
              <span>Total pago</span>
              <span>{formatCurrency(order.total)}</span>
            </div>
          </div>
        </section>
      </div>
    )}
      </Modal>

      <Modal
        isOpen={confirmCancelOpen}
        onClose={handleCloseConfirmCancel}
        title="Cancelar pedido"
        size="sm"
        closeOnEscape={!isCancelling}
        footer={
          <>
            <button
              type="button"
              onClick={handleCloseConfirmCancel}
              disabled={isCancelling}
              className="rounded-full px-4 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-70"
              style={{
                backgroundColor: colors.neutral.gray200,
                color: colors.neutral.gray700,
              }}
            >
              Não cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirmCancel}
              disabled={isCancelling}
              className="rounded-full px-4 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-70"
              style={{
                backgroundColor: colors.status.error,
                color: colors.neutral.white,
              }}
            >
              {isCancelling ? 'Cancelando...' : 'Confirmar cancelamento'}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm" style={{ color: colors.neutral.gray700 }}>
            Tem certeza de que deseja cancelar este pedido? Esta ação é irreversível e não poderá ser desfeita.
          </p>
          <p className="text-xs" style={{ color: colors.neutral.gray500 }}>
            Após confirmar, nossa equipe será notificada e o pedido será cancelado imediatamente.
          </p>
        </div>
      </Modal>
    </>
  )
}

type PaymentViewModalProps = {
  order: Order | null
  isOpen: boolean
  onClose: () => void
  user: any
}

const PaymentViewModal: React.FC<PaymentViewModalProps> = ({ order, isOpen, onClose, user }) => {
  const [paymentData, setPaymentData] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen && order) {
      const fetchPaymentDetails = async () => {
        setIsLoading(true)
        setError(null)
        try {
          const data = await getPaymentDetailsByOrderId(order.id)
          setPaymentData(data)
        } catch (err) {
          console.error('Erro ao buscar detalhes do pagamento:', err)
          setError('Não foi possível carregar os dados de pagamento. Por favor, tente novamente.')
        } finally {
          setIsLoading(false)
        }
      }
      fetchPaymentDetails()
    } else {
      setPaymentData(null)
    }
  }, [isOpen, order])

  if (!order) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={order.paymentMethod?.toLowerCase() === PaymentMethod.PIX || order.paymentMethod?.toLowerCase() === 'pix' ? 'Pagamento com PIX' : 'Pagamento com Boleto'}
      size="md"
    >
      <div className="py-4">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Carregando dados de pagamento...</p>
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-100 bg-red-50 p-6 text-center">
            <p className="text-red-600">{error}</p>
            <button
              onClick={onClose}
              className="mt-4 rounded-full bg-red-600 px-6 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700"
            >
              Fechar
            </button>
          </div>
        ) : (
          <>
            {(order.paymentMethod?.toLowerCase() === PaymentMethod.PIX || order.paymentMethod?.toLowerCase() === 'pix') && user && (
              <PixPayment
                amount={order.total}
                customerEmail={user.email}
                customerName={user.name}
                orderId={order.id}
                existingPaymentData={paymentData ? {
                  pixCode: paymentData.pixCode,
                  qrCodeUrl: paymentData.qrCodeUrl,
                  expiresAt: paymentData.expiresAt || paymentData.dueDate,
                  paymentIntentId: paymentData.transactionId,
                  status: paymentData.status
                } : undefined}
              />
            )}
            {(order.paymentMethod?.toLowerCase() === PaymentMethod.BOLETO || order.paymentMethod?.toLowerCase() === 'boleto') && user && (
              <BoletoPayment
                amount={order.total}
                customerEmail={user.email}
                customerName={user.name}
                customerDocument={user.cpf || ''}
                orderId={order.id}
                existingPaymentData={paymentData ? {
                  boletoUrl: paymentData.boletoUrl,
                  boletoBarcode: paymentData.boletoBarcode,
                  boletoDigitableLine: paymentData.boletoBarcode, // No backend estamos salvando o mesmo em ambos por enquanto ou pegando do identificationField
                  paymentId: paymentData.transactionId,
                  expiresAt: paymentData.dueDate,
                  status: paymentData.status
                } : undefined}
              />
            )}
          </>
        )}
      </div>
    </Modal>
  )
}

export default Orders

